import type { PillTone } from "@/components/ui/pill";
import type {
  CalibrationPendencyKind,
  CalibrationPendencyStatus,
  DivergenceCause,
} from "@/types/closed-loop.types";

// Spec 077 — o vocabulário do CICLO FECHADO. Um lugar só: os mesmos rótulos aparecem no detalhe do
// compromisso, nas duas filas e no histórico, e três traduções paralelas divergem no primeiro
// ajuste de copy.
//
// Status NUNCA só por cor (design system): cada situação carrega cor + dot + rótulo, e o `Pill` com
// `withDot` é o componente que garante os três juntos.

export const CAUSE_LABELS: Record<DivergenceCause, string> = {
  WITHIN_TOLERANCE: "Ficou dentro da faixa",
  EXECUTION_NON_ADHERENCE: "A operação não seguiu o combinado",
  UNMODELED_EVENT: "Aconteceu algo que o modelo não representa",
  INCORRECT_ASSUMPTION: "Uma premissa de parâmetro estava errada",
  STRUCTURAL_MODEL_ERROR: "O motor de simulação está errado",
};

/** O que cada causa AFIRMA — e, nas duas primeiras, o que ela decide sobre a estatística. */
export const CAUSE_HINTS: Record<DivergenceCause, string> = {
  WITHIN_TOLERANCE:
    "O realizado ficou na faixa declarada antes da vigência: nesta janela o gêmeo acertou.",
  EXECUTION_NON_ADHERENCE:
    "A fábrica não executou o que foi comprometido, então a previsão não chegou a ser testada — este compromisso fica fora da estatística de acerto.",
  UNMODELED_EVENT:
    "Houve um acontecimento real que o modelo não representa. Abre trabalho de modelagem.",
  INCORRECT_ASSUMPTION:
    "Um parâmetro que alimenta a simulação estava com valor errado. Abre trabalho de medição.",
  STRUCTURAL_MODEL_ERROR:
    "As premissas estavam certas e o resultado ainda assim não bate: o erro é do motor. Abre trabalho de correção.",
};

/**
 * A aba/fila chama-se pelo TRABALHO (FR-036); a entidade continua sendo "pendência de calibração".
 * Os dois termos convivem de propósito e não são sinônimos soltos.
 */
export const PENDENCY_KIND_LABELS: Record<CalibrationPendencyKind, string> = {
  MODEL_EVENT: "Modelar o acontecimento",
  MEASURE_PARAM: "Medir o parâmetro",
  ENGINE_FIX: "Corrigir o motor",
};

export const PENDENCY_STATUS_LABELS: Record<CalibrationPendencyStatus, string> = {
  OPEN: "Aberta",
  RESOLVED: "Resolvida",
  DISCARDED: "Descartada",
  SUPERSEDED: "Superada",
};

export const PENDENCY_STATUS_TONES: Record<CalibrationPendencyStatus, PillTone> = {
  OPEN: "warning",
  RESOLVED: "success",
  // Descartar é desfecho legítimo, não falha: neutro, e nunca vermelho.
  DISCARDED: "neutral",
  SUPERSEDED: "neutral",
};

export const PENDENCY_STATUS_HINTS: Record<CalibrationPendencyStatus, string> = {
  OPEN: "Espera alguém tratar ou descartar com motivo.",
  RESOLVED: "Alguém tratou e registrou o que mudou no modelo.",
  DISCARDED: "Alguém decidiu não tratar, e disse por quê.",
  SUPERSEDED: "O diagnóstico que a originou foi trocado por outro — ninguém a encerrou.",
};

/** ⚠️ FR-047 — "ainda não classificado" NUNCA é redigido como se fosse uma causa. */
export const NOT_CLASSIFIED_LABEL = "Aguardando classificação";

/** A origem da proposta. Hoje só a regra; a costura já existe para um proponente assistido. */
export function proposalOriginLabel(origin: string): string {
  return origin === "rules" ? "proposta pela regra" : `proposta por ${origin}`;
}
